import React, { useEffect, useMemo, useState } from 'react';
import { Marker, Polygon, Polyline, InfoWindow } from '@react-google-maps/api';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import ProgressBar from 'components/Tvendo/Common/ProgressBar';
import GridContainer from 'components/Grid/GridContainer';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItem from '@material-ui/core/ListItem';
import GridItem from 'components/Grid/GridItem';
import List from '@material-ui/core/List';
import { formatDate } from 'hooks/Pipe';
import { Autorenew, GpsFixed, Person } from '@material-ui/icons';
import { Checkbox, FormControlLabel, ListItemText, Tooltip } from '@material-ui/core';
import GoogleMapCustom from 'components/Tvendo/Common/GoogleMapCustom';
import LayerCombo from 'components/Tvendo/Common/LayerCombo';
import useLayerContext from 'contexts/layerContext';
import { wsGetAll } from 'services/WService';
import moment from 'moment';

function SupervisorTracking(props) {
  const { layers, selectedDivition, selectedLayer, setSelectedLayer } = useLayerContext();
  const [processing, setProcessing] = useState(false);

  const { supervisor } = props;
  const [infoWindowData, setInfoWindowData] = useState();
  const [workDay, setWorkDay] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: -14.07543457346045,
    lng: -75.73423487506565,
  });
  const [mapZoom, setMapZoom] = useState(13);
  const [dataPolyline, setDataPolyline] = useState([]);
  const [showGps, setShowGps] = useState(true);
  const [dataSellerGps, setDataSellerGps] = useState();
  const [sellerRoutes, setSellerRoutes] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState();
  const [infoData, setInfoData] = useState({
    show: false,
    title: '',
    description: '',
  });

  const sellers = useMemo(() => {
    if (supervisor.sellers === undefined) return [];
    return supervisor.sellers;
  }, [supervisor]);

  const routes = useMemo(() => {
    if (supervisor.routes === undefined) return [];
    if (selectedLayer === undefined || selectedLayer.id === undefined) return supervisor.routes;
    return supervisor.routes.filter((route) => parseInt(route.base_layer_id) === selectedLayer.id);
  }, [supervisor.routes, selectedLayer]);

  async function onSellerClick(seller) {
    if (processing) return;

    setDataSellerGps(undefined);
    setSellerRoutes([]);

    setProcessing(true);
    setSelectedSeller(seller);
    let res = await wsGetAll(
      `ordm/gps?base_seller_id=${seller.id}&date=${moment().format('YYYY-MM-DD')}`,
    );
    setProcessing(false);
    if (res.success) {
      let routesIds = res.data.routes.map((route) => route.id);
      setSellerRoutes(routesIds);
      setDataSellerGps(
        res.data.gps.map((gps) => {
          return {
            lat: parseFloat(gps.latitude),
            lng: parseFloat(gps.longitude),
          };
        }),
      );
    }
  }

  useEffect(() => {
    if (supervisor === undefined || supervisor.id === undefined) {
      return;
    }
    setWorkDay(supervisor.workDay);
    if (supervisor.workDay !== null) {
      setDataPolyline(
        supervisor.workDay.gps
          .filter((gps) => gps.latitude !== '0')
          .map((gps) => {
            return {
              lat: parseFloat(gps.latitude),
              lng: parseFloat(gps.longitude),
            };
          }),
      );
    }
  }, [supervisor]);

  // Centro del mapa
  useEffect(() => {
    if (workDay === null) return;
    if (workDay.gps.length > 0) {
      setMapCenter({
        lat: parseFloat(workDay.gps[0].latitude),
        lng: parseFloat(workDay.gps[0].longitude),
      });
    } else if (routes.length > 0) {
      setMapCenter({
        lat: parseFloat(routes[0].centroid.value[1]),
        lng: parseFloat(routes[0].centroid.value[0]),
      });
    }
  }, [workDay, routes]);

  if (workDay === null) {
    return <>El supervisor no tiene jornada</>;
  }

  return (
    <GridContainer>
      {/* Progreso de jornada */}
      <GridItem xs={12}>
        <ProgressBar progress={parseInt(supervisor.progress_percentage)} />
        <p>
          S/{parseFloat(supervisor.progress).toFixed(2)} de S/
          {parseFloat(supervisor.quota).toFixed(2)}
        </p>
      </GridItem>
      <GridItem xs={12}>
        <GridContainer>
          {/* Mapa */}
          <GridItem xs={8}>
            <GoogleMapCustom
              mapContainerStyle={{
                width: '100%',
                height: '100%',
                minHeight: '700px',
              }}
              zoom={mapZoom}
              center={mapCenter}
            >
              {showGps && workDay.gps.length > 0 && (
                <>
                  {/* Inicio */}
                  <Marker
                    onClick={() => setInfoWindowData(workDay.gps[0])}
                    position={{
                      lat: parseFloat(workDay.gps[0].latitude),
                      lng: parseFloat(workDay.gps[0].longitude),
                    }}
                    icon={'/img/ping.png'}
                  />
                  {/* Final */}
                  <Marker
                    onClick={() => setInfoWindowData(workDay.gps[workDay.gps.length - 1])}
                    position={{
                      lat: parseFloat(workDay.gps[workDay.gps.length - 1].latitude),
                      lng: parseFloat(workDay.gps[workDay.gps.length - 1].longitude),
                    }}
                    icon={'/img/pingEnd.png'}
                  />
                </>
              )}

              {showGps &&
                workDay.gps.map((gps) => {
                  return (
                    <Marker
                      key={gps.id}
                      onClick={() => setInfoWindowData(gps)}
                      position={{
                        lat: parseFloat(gps.latitude),
                        lng: parseFloat(gps.longitude),
                      }}
                      icon={'/img/point.png'}
                    />
                  );
                })}

              {showGps && workDay.gps.length > 2 && (
                <Polyline
                  path={dataPolyline}
                  options={{
                    strokeColor: 'red',
                    strokeOpacity: 0.5,
                    strokeWeight: 3,
                    visible: true,
                  }}
                />
              )}

              {dataSellerGps !== undefined && (
                <>
                  {dataSellerGps.length > 0 && (
                    <>
                      {/* Inicio */}
                      <Marker position={dataSellerGps[0]} icon="/img/ping.png" />
                      {/* Final */}
                      <Marker
                        position={dataSellerGps[dataSellerGps.length - 1]}
                        icon="/img/pingEnd.png"
                      />
                    </>
                  )}
                  <Polyline
                    path={dataSellerGps}
                    options={{
                      strokeColor: '#001FFF',
                      strokeOpacity: 0.5,
                      strokeWeight: 3,
                      visible: true,
                    }}
                  />
                </>
              )}

              {/* InfoWindow */}
              {infoWindowData !== undefined && (
                <InfoWindow
                  position={{
                    lat: parseFloat(infoWindowData.latitude),
                    lng: parseFloat(infoWindowData.longitude),
                  }}
                  onCloseClick={() => setInfoWindowData(undefined)}
                  options={{
                    pixelOffset: {
                      width: 0,
                      height: -25,
                    },
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <li>
                      <span>Hora: </span>
                      {formatDate(infoWindowData.created_at, 'H')}
                    </li>
                  </div>
                </InfoWindow>
              )}

              {routes.map((route) => {
                return (
                  <Polygon
                    key={route.id}
                    paths={route.geometry}
                    options={{
                      fillColor: sellerRoutes.includes(route.id) ? 'blue' : route.layer.color,
                    }}
                    onDblClick={(e) => {
                      setInfoData({
                        show: true,
                        lat: route.centroid.value[1],
                        lng: route.centroid.value[0],
                        title: route.code,
                        description: route.description,
                      });
                    }}
                  />
                );
              })}

              {infoData.show && (
                <InfoWindow
                  position={{ lat: infoData.lat, lng: infoData.lng }}
                  onCloseClick={() => setInfoData({ show: false, description: '', title: '' })}
                  options={{
                    pixelOffset: {
                      width: 0,
                      height: -25,
                    },
                  }}
                >
                  <div>
                    <h4>{infoData.title}</h4>
                    <h5>{infoData.description}</h5>
                  </div>
                </InfoWindow>
              )}
            </GoogleMapCustom>
          </GridItem>
          <GridItem xs={4}>
            <CardContainer
              title="Configuración"
              cardBodyStyle={{
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              <FormControlLabel
                label="Ver gps supervisor"
                control={
                  <Checkbox checked={showGps} onChange={(e) => setShowGps(e.target.checked)} />
                }
              />
              <LayerCombo isFilter />
            </CardContainer>
            {/* Vendedores */}
            <CardContainer
              title="Vendedores"
              cardBodyStyle={{
                maxHeight: '300px',
                overflow: 'auto',
              }}
            >
              <List>
                {sellers.map((seller) => {
                  return (
                    <Tooltip title="Comparar" placement="bottom-start">
                      <ListItem button key={seller.id} onClick={() => onSellerClick(seller)}>
                        <ListItemIcon
                          style={{
                            color: seller.id === selectedSeller?.id ? '#00c369' : undefined,
                          }}
                        >
                          {seller.id === selectedSeller?.id && processing ? (
                            <Autorenew />
                          ) : (
                            <Person />
                          )}
                        </ListItemIcon>
                        <ListItemText>{seller.SlpName}</ListItemText>
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </List>
            </CardContainer>
            {/* GPS */}
            {!!supervisor.workDay && (
              <CardContainer
                title="Gps"
                cardBodyStyle={{
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                <List>
                  {supervisor.workDay.gps.map((gps, index) => {
                    return (
                      <ListItem button key={gps.id} onClick={() => setInfoWindowData(gps)}>
                        <ListItemIcon>
                          <GpsFixed />
                        </ListItemIcon>
                        <ListItemText>
                          Hora: {formatDate(gps.created_at, 'H')}
                          <br />
                          Lat: {parseFloat(gps.latitude).toFixed(4)} || Lng:{' '}
                          {parseFloat(gps.longitude).toFixed(4)}
                        </ListItemText>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContainer>
            )}
          </GridItem>
        </GridContainer>
      </GridItem>
    </GridContainer>
  );
}

export default SupervisorTracking;
